import React from 'react';

import ReactDOM from 'react-dom';
import classnames from 'classnames';

import { ReactComponent as CloseIcon } from 'assets/icons/menu/close.svg';
import Button from 'clean/components/common/Button/Button';
import Loader from 'clean/components/common/Loader/Loader';

import './ModalDialog.scoped.scss';

const modalElement = document.getElementById('modal');

interface Props {
  children?: React.ReactNode;
  open: boolean;
  error?: string;
  onClose: () => void;
  title?: React.ReactNode;
  actions?: (React.ReactNode | IRenderAbleNode)[];
  actionsJustifyContent?: string;
  isLoadingContent?: boolean;
  isLoadingActions?: boolean;
  hasContentPadding?: boolean;
}

const ModalDialog: React.FC<Props> = ({
  open,
  error = '',
  onClose,
  children,
  title = null,
  actions = [],
  isLoadingContent = false,
  isLoadingActions = false,
  hasContentPadding = true,
  actionsJustifyContent = 'center',
}) => {
  if (!open) {
    return null;
  }

  return ReactDOM.createPortal(
    <div className="modal-dialog">
      <div className="backdrop" onClick={onClose} />
      {!isLoadingContent && (
        <div
          className="modal"
          onClick={e => {
            e.stopPropagation();
          }}
        >
          <div className="close-icon" onClick={onClose}>
            <CloseIcon />
          </div>

          {title && <div className="title">{title}</div>}

          {isLoadingContent && (
            <div className="loaderContainer">
              <Loader />
            </div>
          )}

          <div
            className={classnames('content', { contentPadding: hasContentPadding })}
            style={{
              height: isLoadingContent ? '1px' : 'auto',
              visibility: isLoadingContent ? 'hidden' : 'visible',
              opacity: isLoadingActions ? '0.5' : '1',
              pointerEvents: isLoadingActions ? 'none' : 'auto',
            }}
          >
            {children}
          </div>

          {error.length > 0 && <div className="error">{error}</div>}

          {actions.length > 0 && (
            <div
              className="actions"
              style={{
                justifyContent: actionsJustifyContent,
                opacity: isLoadingActions ? '0.5' : '1',
                pointerEvents: isLoadingActions ? 'none' : 'auto',
              }}
            >
              {actions.map((entry, index) => {
                if (React.isValidElement(entry)) {
                  return entry;
                }

                const { type, label, handler, colour, fullWidth, startIcon } =
                  entry as IRenderAbleNode;

                return (
                  <Button
                    key={`${label}_${index}`}
                    startIcon={startIcon}
                    color={colour}
                    variant={type}
                    fullWidth={fullWidth}
                    label={label}
                    onClick={handler}
                  />
                );
              })}
            </div>
          )}
        </div>
      )}

      {isLoadingContent && (
        <div className="loaderContainer">
          <Loader />
        </div>
      )}
    </div>,
    modalElement!
  );
};

export default ModalDialog;
